import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchEpicPostDeploy } from '../../lib/api/epics';
import type { PostDeployEvent, PostDeployRun } from '../../lib/api/epics';
import { StatusBadge } from '../ui/StatusBadge';
import type { StatusTone } from '../ui/StatusBadge';

/**
 * FORGE-407 — post-deploy checks: integration tests, the CloudWatch log review
 * (each MCP call with its duration, the verdict, findings) and runtime fix loops,
 * as one timeline per epic. Null-discipline: an epic that never reached the
 * post-deploy phase renders nothing.
 */

const VERDICT_TONE: Record<string, StatusTone> = { CLEAN: 'success', FINDINGS: 'danger', INCONCLUSIVE: 'warning' };
const SEV_TONE: Record<string, StatusTone> = { critical: 'danger', high: 'danger', medium: 'warning', low: 'info', info: 'neutral' };
const DOT: Record<PostDeployEvent['tone'], string> = {
  success: 'bg-success-500', danger: 'bg-danger-500', warning: 'bg-warning-500', info: 'bg-primary-500', neutral: 'bg-slate-400',
};

function when(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString();
}

const RunHeader: React.FC<{ run: PostDeployRun }> = ({ run }) => (
  <div className="flex flex-wrap items-center gap-2">
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{run.env}</span>
    {run.integration && (
      <StatusBadge tone={run.integration.failed > 0 ? 'danger' : 'success'}>
        {run.integration.passed}/{run.integration.total} integration tests
      </StatusBadge>
    )}
    {run.review ? (
      <StatusBadge tone={run.review.verdict ? VERDICT_TONE[run.review.verdict] : 'info'}>
        {run.review.verdict ? `log review ${run.review.verdict.toLowerCase()}` : 'log review running'}
      </StatusBadge>
    ) : (
      <StatusBadge tone="neutral">log review not run</StatusBadge>
    )}
    {run.review?.downgraded_from && <StatusBadge tone="warning">downgraded from {run.review.downgraded_from.toLowerCase()}</StatusBadge>}
  </div>
);

export const PostDeployChecks: React.FC<{ epicId: string }> = ({ epicId }) => {
  const query = useQuery({
    queryKey: ['epic-post-deploy', epicId],
    queryFn: () => fetchEpicPostDeploy(epicId),
    retry: false,
  });
  const data = query.data;
  if (!data || data.runs.length === 0) return null;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4" data-testid="post-deploy-checks">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">🔎 Post-deploy checks</h3>
        {data.fix_loops > 0 && (
          <span className="text-xs text-gray-500">{data.fix_loops} runtime fix loop{data.fix_loops === 1 ? '' : 's'} used</span>
        )}
      </div>

      <div className="mb-3 space-y-2">
        {data.runs.map((r) => <RunHeader key={r.env} run={r} />)}
      </div>

      <ol className="space-y-2 border-l border-gray-200 pl-4">
        {data.timeline.map((e, i) => (
          <li key={`${e.env}-${e.kind}-${i}`} className="relative">
            <span className={`absolute -left-[21px] top-1.5 h-2 w-2 rounded-full ${DOT[e.tone]}`} />
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-xs text-gray-800">{e.label}</span>
              <span className="shrink-0 font-mono text-[11px] text-gray-400">{when(e.at)}</span>
            </div>
            {e.detail && <div className="text-[11px] text-gray-500">{e.detail}</div>}
          </li>
        ))}
      </ol>

      {data.runs.some((r) => r.findings.length > 0) && (
        <div className="mt-4">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Runtime findings</div>
          <div className="divide-y divide-gray-100">
            {data.runs.flatMap((r) => r.findings.map((f, i) => (
              <div key={`${r.env}-${i}`} className="py-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-xs text-gray-700">{f.pattern}</span>
                  <span className="flex shrink-0 items-center gap-2">
                    <StatusBadge tone={SEV_TONE[f.severity] ?? 'neutral'}>{f.severity}</StatusBadge>
                    <span className="font-mono text-[11px] text-gray-500">×{f.count}</span>
                  </span>
                </div>
                <code className="block truncate font-mono text-[11px] text-gray-400">{f.log_group}</code>
              </div>
            )))}
          </div>
        </div>
      )}
    </div>
  );
};
